import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Icon from '../components/common/Icon';
import Breadcrumbs from '../components/common/Breadcrumbs';
import SectionHeader from '../components/common/SectionHeader';
import NewsCard from '../components/common/NewsCard';
import EventCard from '../components/common/EventCard';
import Toolbar from '../components/common/Toolbar';
import SearchBar from '../components/common/SearchBar';
import FilterSelect from '../components/common/FilterSelect';

const circoloData = {
  trastevere: {
    title: 'GD Trastevere',
    heroImage: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?q=80&w=1600&auto=format&fit=crop',
    head: { 
      name: 'Marco Tosi', 
      role: 'Segretario di Circolo', 
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=400&auto=format&fit=crop',
      bio: "Attivista radicato nel rione, Marco coordina il circolo dal 2022. Crede che la politica debba partire dalle strade e dai bisogni quotidiani dei cittadini."
    },
    description: "Uno dei circoli storici dei Giovani Democratici di Roma. Un punto di riferimento per la partecipazione politica nel cuore del I Municipio.",
    color: 'var(--color-primary)',
    icon: 'MapPin',
    stats: [
      { label: 'Iscritti', value: '45' },
      { label: 'Fondazione', value: '2012' },
      { label: 'Federazione', value: 'Roma' }
    ],
    tasks: [
      { title: 'Riunioni Aperte', text: 'Incontri settimanali aperti a tutti i giovani del quartiere.', icon: 'Users' },
      { title: 'Battaglie Locali', text: 'Iniziative su casa, decoro e servizi municipali.', icon: 'Flag' },
      { title: 'Formazione', text: 'Seminari di approfondimento politico e sociale.', icon: 'BookOpen' },
      { title: 'Volontariato', text: 'Azioni dirette di supporto alla comunità locale.', icon: 'Heart' }
    ],
    news: [
      { id: 1, title: 'Trastevere: serata dibattito sulla casa', date: '14 Mar 2026', category: 'Attualità', excerpt: 'Un confronto aperto con esperti e amministratori sul tema dell\'emergenza abitativa nel centro storico.', image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=600' },
      { id: 2, title: 'Volontariato nel quartiere: successo dell\'iniziativa', date: '01 Mar 2026', category: 'Territorio', excerpt: 'Racconto della giornata di pulizia e riqualificazione delle aree verdi del rione.', image: 'https://images.unsplash.com/photo-1559027615-cd9673980d52?q=80&w=600' }
    ],
    events: [
      { id: 101, title: 'Assemblea di Circolo', day: '25', month: 'Mag', location: 'Sede PD Trastevere', time: '19:00', badge: 'Riunione', image: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=600' }
    ],
    documents: [
      { title: 'Verbale Assemblea Marzo', type: 'Verbale', date: '15 Mar 2026', badge: 'PDF', size: '1.2 MB' },
      { title: 'Programma Attività 2026', type: 'Documento', date: '10 Gen 2026', badge: 'PDF', size: '2.5 MB' }
    ]
  }
};

const CircoloPage = () => {
  const { id } = useParams();
  const data = circoloData[id] || circoloData.trastevere;

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Circoli', path: '/circoli' },
    { label: data.title }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  return (
    <Layout>
      {/* EPIC HERO SECTION */}
      <section className="hero--epic">
        <div className="hero__bg">
          {data.heroImage ? (
            <div className="hero__image" style={{ backgroundImage: `url(${data.heroImage})` }}></div>
          ) : (
            <div className="hero__image" style={{ background: 'linear-gradient(135deg, var(--color-primary) 0%, #450a0a 100%)' }}></div>
          )}
          <div className="hero__overlay" style={{ background: `linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(9,9,11,0.95) 100%)` }}></div>
        </div>
        
        <div className="hero__content-epic container" style={{ color: '#fff' }}>
          <div className="reveal" style={{ marginBottom: 'var(--space-8)' }}>
            <Breadcrumbs items={breadcrumbs} style={{ color: 'rgba(255,255,255,0.7)' }} />
          </div>
          
          <div className="reveal mb-6">
            <span className="badge" style={{ background: data.color, color: '#fff', border: 'none', padding: 'var(--space-2) var(--space-4)', fontSize: 'var(--text-xs)' }}>
              Circolo Territoriale
            </span>
          </div>
          
          <h1 className="hero__title-epic text-reveal-trigger" style={{ color: '#fff', fontSize: 'clamp(3rem, 8vw, 5rem)' }}>
            <span className="line text-reveal"><span className="word text-reveal__inner">{data.title}</span></span>
          </h1>
          
          <p className="hero__subtitle-epic reveal" style={{ color: 'rgba(255,255,255,0.9)', maxWidth: '700px', fontSize: 'var(--text-body-lg)' }}>
            {data.description}
          </p>
          
          <div className="reveal mt-8 flex gap-6">
            {data.stats.map((stat, i) => (
              <div key={i} className="flex flex-col">
                <span style={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', opacity: 0.6, letterSpacing: '0.05em' }}>{stat.label}</span>
                <span style={{ fontSize: 'var(--text-lg)', fontWeight: 600 }}>{stat.value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEGRETARIO & ACTIVITIES SECTION */}
      <section className="section" style={{ marginTop: '-100px', position: 'relative', zIndex: 10, paddingTop: 0 }}>
        <div className="container">
          <div className="reveal reveal--scale mb-12">
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.8)', 
              backdropFilter: 'blur(20px)',
              padding: 'var(--space-8)', 
              borderRadius: 'var(--radius-3xl)',
              boxShadow: '0 30px 60px rgba(0,0,0,0.12)',
              border: '1px solid rgba(255,255,255,0.3)',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 'var(--space-10)'
            }}>
              <div style={{ width: '180px', height: '180px', borderRadius: 'var(--radius-2xl)', overflow: 'hidden', boxShadow: 'var(--shadow-lg)' }}>
                <img src={data.head.image} alt={data.head.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div style={{ flex: 1, minWidth: '300px' }}>
                <span className="text-overline mb-2 block" style={{ color: data.color }}>{data.head.role}</span>
                <h2 className="h2 mb-4">{data.head.name}</h2>
                <p className="text-body text-secondary mb-6 italic" style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
                  "{data.head.bio}"
                </p>
                <div className="flex gap-4">
                  <Link to="/partecipa" className="btn btn--primary">Partecipa al Circolo</Link>
                  <div className="flex gap-3 ml-4 items-center">
                    <Link to="#" className="text-secondary hover:text-primary transition-colors"><Icon name="Mail" size={20} /></Link>
                    <Link to="#" className="text-secondary hover:text-primary transition-colors"><Icon name="Instagram" size={20} /></Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* NEWS SECTION */}
      <section className="section bg-subtle">
        <div className="container">
          <SectionHeader overline="Vita di Circolo" title="Ultime Notizie" className="mb-8" />
          
          <Toolbar
            className="mb-10 reveal"
            left={<SearchBar placeholder="Cerca tra le notizie..." ariaLabel="Cerca notizie circolo" />}
            right={<FilterSelect label="Categoria" options={['Tutte', 'Iniziative', 'Attualità', 'Territorio']} />}
          />

          <div className="grid grid-3 gap-8">
            {data.news.map((n, i) => (
              <NewsCard 
                key={n.id} 
                news={n} 
                title={n.title}
                excerpt={n.excerpt}
                badge={n.category}
                meta={[n.date]}
                image={n.image}
                className={`reveal stagger-${i + 1}`} 
              />
            ))}
          </div>
        </div>
      </section>

      {/* EVENTS SECTION */}
      <section className="section bg-white border-bottom">
        <div className="container">
          <SectionHeader overline="Agenda" title="I nostri prossimi appuntamenti" className="mb-8" />
          
          <Toolbar className="mb-8 reveal"
            left={<FilterSelect label="Tipo" options={['Tutti', 'Assemblea', 'Evento Pubblico', 'Banchetto']} />}
            right={<button className="btn btn--outline btn--sm">📅 Calendario</button>}
          />

          <div className="grid grid-3 gap-8">
            {data.events.map((e, i) => (
              <EventCard 
                key={e.id} 
                {...e}
                className={`reveal stagger-${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* DOCUMENTS SECTION */}
      <section className="section bg-subtle">
        <div className="container">
          <SectionHeader overline="Trasparenza" title="Documenti e Verbali" className="mb-8" />
          
          <Toolbar className="mb-8 reveal"
            left={<SearchBar placeholder="Cerca documento..." ariaLabel="Cerca" />}
          />

          <div className="flex flex-col gap-4 reveal">
            {data.documents.map((d, i) => (
              <a 
                href="#" 
                className={`card flex justify-between items-center hover:border-primary transition-all stagger-${i + 1}`} 
                style={{ padding: 'var(--space-6)', background: '#fff', border: '1px solid var(--color-border-subtle)', borderRadius: 'var(--radius-xl)' }} 
                key={d.title}
              >
                <div style={{ flex: 1 }}>
                  <span className="badge badge--subtle mb-2" style={{ color: data.color, background: `${data.color}10` }}>{d.type}</span>
                  <h4 className="h5 mb-1">{d.title}</h4>
                  <span className="text-xs text-secondary uppercase tracking-wider">{d.date} • {d.size}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="badge badge--primary" style={{ background: data.color }}>{d.badge}</span>
                  <div className="btn btn--icon btn--sm btn--outline"><Icon name="Download" size={16} /></div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

    </Layout>
  );
};

export default CircoloPage;
